import { z } from 'zod';

import {
  type AnswerReading,
  readAnswerDocument,
  refusedShape,
  unfencedAnswer,
} from './answer-document.ts';
import { type AnswerWire, answerWire } from './answer-wire.ts';
import type { DgContent, DgQuestionType, ResearchQuestion } from './artifacts.ts';
import {
  BRIEF_CARD_QUOTE_LIMIT,
  MAX_BRIEF_ENTRIES,
  MAX_BRIEF_HEADLINE_CHARS,
  MAX_BRIEF_LIST_ITEMS,
  MAX_BRIEF_PASSAGE_CHARS,
  type NotSettledKind,
} from './brief.ts';
import {
  type BriefToken,
  type CountRef,
  MAX_RICH_TEXT_SEGMENTS,
  type RichTextSegment,
  gatedBriefProse,
} from './brief-counts.ts';
import {
  type RqEvidenceState,
  type StudyEvidence,
  type ThemesUnavailableReason,
  rqEvidenceState,
} from './brief-evidence.ts';
import { briefThemesView } from './brief-themes.ts';

/** One poll as a synthesis is shown it: `POLL3`, and each option `POLL3.2`. */
export interface BriefPollHandle {
  handle: string;
  dg_question_id: string;
  question_text: string;
  multi_select: boolean;
  respondents: number;
  options: { handle: string; option_id: string; text: string; count: number }[];
}

/** One theme as a synthesis is shown it: `THEME2`, under the research question it belongs to. */
export interface BriefThemeHandle {
  handle: string;
  rq_id: string;
  theme_id: string;
  label: string;
  participants: number;
  /** The guide question it was coded on, where another kept theme repeats its label. */
  source?: string;
}

/** One quote as a synthesis is shown it, under the handle the evidence gave it (`Q12`). */
export interface BriefQuoteHandle {
  handle: string;
  rq_id: string;
  quote_id: string;
  participant: string;
  dg_question_id: string;
  text: string;
}

/**
 * The image a guide question shows, as a synthesis is told it: the name the researcher gave its
 * file and, in a paired intake, the label of the version it is the file of (`null` otherwise).
 */
export interface BriefShownImage {
  name: string;
  variant: string | null;
}

/** One research question as a synthesis is shown it: `RQ1`, and what its evidence holds. */
export interface BriefRqHandle {
  handle: string;
  rq_id: string;
  text: string;
  decision: string;
  state: RqEvidenceState;
  /**
   * Its guide questions, each with whether it reached the conversation and the image participants
   * were shown just before it — `null` when it shows none, or shows one whose file was not read.
   */
  served: {
    dg_question_id: string;
    type: DgQuestionType;
    text: string;
    placed: boolean;
    shows: BriefShownImage | null;
  }[];
  /** Its polls' handles, in guide order. */
  polls: string[];
  /** Its kept themes: shown and "+K more" handles, how many people were coded, or why there are none. */
  themes: BriefRqThemes;
  /** Its quotes' handles, in evidence order. */
  quotes: string[];
}

/**
 * A research question's themes as a synthesis is shown them (`briefThemesView`): available, with
 * the handles its themes visual shows, the handles behind its "+K more", the floor and how many
 * themes were left out under it, and how many people were coded; or unavailable, with why — none of which is
 * zero themes. One field, so a state and its handles cannot disagree.
 */
export type BriefRqThemes =
  | {
      status: 'available';
      /** The handles the themes visual shows, ranked. */
      handles: string[];
      /** The kept handles behind "+K more", ranked after `handles`. */
      more: string[];
      /** The fewest people a kept theme is raised by. */
      floor: number;
      /** How many themes fell below `floor`: numbered nowhere. */
      left_out: number;
      coded_participants: number;
    }
  | { status: 'unavailable'; reason: ThemesUnavailableReason };

/** A research question's shown theme handles, ranked: none when its themes are unavailable. */
export const rqThemeHandles = (rq: Pick<BriefRqHandle, 'themes'>): string[] =>
  rq.themes.status === 'available' ? rq.themes.handles : [];

/** A research question's kept theme handles, shown then "+K more": none when unavailable. */
export const rqKeptThemeHandles = (rq: Pick<BriefRqHandle, 'themes'>): string[] =>
  rq.themes.status === 'available' ? [...rq.themes.handles, ...rq.themes.more] : [];

/**
 * The one map between the handles a synthesis reads and writes and the ids they stand for, used by
 * the prompt that shows the evidence and by the parser that reads the answer, so the two cannot
 * number anything differently. The model never writes an id.
 */
export interface BriefHandles {
  rqs: BriefRqHandle[];
  polls: ReadonlyMap<string, BriefPollHandle>;
  themes: ReadonlyMap<string, BriefThemeHandle>;
  quotes: ReadonlyMap<string, BriefQuoteHandle>;
}

/**
 * Numbers the evidence for a synthesis: research questions `RQ1`… in purchased order; polls
 * `POLL1`… across the study in order of first appearance, a poll serving two research questions
 * keeping one handle, with options `POLL1.1`… in the guide's order; themes `THEME1`… across the
 * study, each research question's kept themes in `briefThemesView`'s order (a theme left out under
 * the floor gets no handle); quotes under their own `Q` handles. Each served question says what image it shows from the
 * guide's own stimulus and `stimuli`, the shown files read by id. Expects evidence
 * `validateStudyEvidence` accepted for these research questions and this guide.
 */
export const briefHandles = (
  evidence: StudyEvidence,
  accepted: {
    rqs: readonly ResearchQuestion[];
    dg: DgContent;
    stimuli?: ReadonlyMap<string, BriefShownImage>;
  },
): BriefHandles => {
  const questions = new Map(
    accepted.dg.sections
      .flatMap((section) => section.questions)
      .map((question) => [question.id, question]),
  );
  const polls = new Map<string, BriefPollHandle>();
  const pollByQuestion = new Map<string, string>();
  const themes = new Map<string, BriefThemeHandle>();
  const quotes = new Map<string, BriefQuoteHandle>();

  const rqs = evidence.rqs.map((rq, index): BriefRqHandle => {
    const accepting = accepted.rqs.find((each) => each.id === rq.rq_id);

    const pollHandles = rq.polls.map((poll) => {
      const known = pollByQuestion.get(poll.dg_question_id);
      if (known !== undefined) {
        return known;
      }
      const handle = `POLL${String(polls.size + 1)}`;
      const question = questions.get(poll.dg_question_id);
      polls.set(handle, {
        handle,
        dg_question_id: poll.dg_question_id,
        question_text: question?.text ?? '',
        multi_select: poll.multi_select,
        respondents: poll.respondents,
        options: poll.options.map((option, position) => ({
          handle: `${handle}.${String(position + 1)}`,
          option_id: option.option_id,
          text: question?.options?.find((each) => each.id === option.option_id)?.text ?? '',
          count: option.count,
        })),
      });
      pollByQuestion.set(poll.dg_question_id, handle);
      return handle;
    });

    let themeHandles: BriefRqThemes;
    if (rq.themes.status === 'available') {
      const view = briefThemesView(rq.themes, accepted.dg);
      const kept = view.kept.map((theme) => {
        const handle = `THEME${String(themes.size + 1)}`;
        themes.set(handle, { handle, rq_id: rq.rq_id, ...theme });
        return handle;
      });
      themeHandles = {
        status: 'available',
        coded_participants: view.coded_participants,
        handles: kept.slice(0, view.shown),
        more: kept.slice(view.shown),
        floor: view.floor,
        left_out: view.left_out,
      };
    } else {
      themeHandles = { status: 'unavailable', reason: rq.themes.reason };
    }

    for (const quote of rq.quotes) {
      quotes.set(quote.quote_id, {
        handle: quote.quote_id,
        rq_id: rq.rq_id,
        quote_id: quote.quote_id,
        participant: quote.participant,
        dg_question_id: quote.dg_question_id,
        text: quote.text,
      });
    }

    return {
      handle: `RQ${String(index + 1)}`,
      rq_id: rq.rq_id,
      text: accepting?.text ?? '',
      decision: accepting?.decision ?? '',
      state: rqEvidenceState(rq),
      served: rq.served.map((served) => {
        const question = questions.get(served.dg_question_id);
        const shown =
          question?.stimulus === undefined
            ? undefined
            : accepted.stimuli?.get(question.stimulus.file_id);

        return {
          dg_question_id: served.dg_question_id,
          type: served.type,
          text: question?.text ?? '',
          placed: served.placed,
          shows: shown === undefined ? null : { name: shown.name, variant: shown.variant },
        };
      }),
      polls: pollHandles,
      themes: themeHandles,
      quotes: rq.quotes.map((quote) => quote.quote_id),
    };
  });

  return { rqs, polls, themes, quotes };
};

/**
 * A passage the synthesis wrote, read: its words, and each token resolved to what it names. The
 * values are filled in from the evidence when the Brief is composed.
 */
export type BriefProseSegment =
  | { t: 'text'; text: string }
  | { t: 'count'; ref: CountRef }
  | { t: 'of'; poll: string }
  | { t: 'option'; poll: string; option: string };
export type BriefProse = BriefProseSegment[];

/** A card's visual: one of its polls, its themes, or none. */
export type BriefAnswerVisual =
  { kind: 'poll'; dg_question_id: string } | { kind: 'themes' } | null;

/** A synthesis's answer, read: every handle mapped to the id it stands for. */
export interface BriefAnswer {
  verdict: { headline: BriefProse; sub_verdict: BriefProse };
  summary: BriefProse[];
  recommendations: BriefProse[];
  not_settled: {
    rq_id: string | null;
    kind: NotSettledKind;
    what: BriefProse | null;
    follow_up: BriefProse;
  }[];
  cards: {
    rq_id: string;
    status: 'answered' | 'not_settled';
    headline: BriefProse;
    detail: BriefProse;
    visual: BriefAnswerVisual;
    quote_ids: string[];
  }[];
}

/** The answer a synthesis gives, as its instructions show it. */
export const BRIEF_ANSWER_EXAMPLE =
  '{"verdict": {"headline": "<one sentence>", "sub_verdict": "<one or two sentences>"}, "summary": ["<a sentence>", ...], "recommendations": ["<a sentence>", ...], "not_settled": [{"rq": "RQ2" | null, "what": "<what is not settled>" | null, "follow_up": "<how to settle it>"}, ...], "cards": [{"rq": "RQ1", "status": "answered" | "not_settled", "headline": "<one sentence>", "detail": "<a few sentences>", "visual": "POLL3" | "THEMES" | null, "quotes": ["Q12", ...]}, ...]}';

/** What a card's `visual` names when it shows its research question's themes. */
export const BRIEF_THEMES_VISUAL = 'THEMES';

/**
 * The answer format's shape with `rq` the type of every research question it names and `visual`
 * the type of a card's visual: every key required, every object closed, and no bound.
 */
const briefAnswerShape = <Rq extends z.ZodType<string>, Visual extends z.ZodType<string>>(
  rq: Rq,
  visual: Visual,
) =>
  z.strictObject({
    verdict: z.strictObject({ headline: z.string(), sub_verdict: z.string() }),
    summary: z.array(z.string()),
    recommendations: z.array(z.string()),
    not_settled: z.array(
      z.strictObject({ rq: rq.nullable(), what: z.string().nullable(), follow_up: z.string() }),
    ),
    cards: z.array(
      z.strictObject({
        rq,
        status: z.enum(['answered', 'not_settled']),
        headline: z.string(),
        detail: z.string(),
        visual: visual.nullable(),
        quotes: z.array(z.string()),
      }),
    ),
  });

/**
 * The answer format's shape for any study, which the parser's `answerSchema` adds its list bounds
 * to: research questions and visuals are any string, which the parser holds to the handles.
 */
export const BRIEF_ANSWER_WIRE_SHAPE = briefAnswerShape(z.string(), z.string());

/**
 * The answer format's shape for one synthesis, as the provider is asked to hold it
 * (`briefAnswerWire`): every research question it names is one of `handles`' (`RQ1`, …), and a
 * card's visual one of the study's polls or `THEMES`. Quotes stay any string, since a study can
 * hold more of them than a strict provider takes as one enum; the parser holds them, the card each
 * visual and quote belongs to, and every other rule.
 */
export const briefAnswerWireShape = (handles: BriefHandles) => {
  const [firstRq, ...otherRqs] = handles.rqs.map((rq) => rq.handle);

  return briefAnswerShape(
    firstRq === undefined ? z.string() : z.enum([firstRq, ...otherRqs]),
    z.enum([BRIEF_THEMES_VISUAL, ...handles.polls.keys()]),
  );
};

/** The wire a synthesis over `handles` answers under. */
export const briefAnswerWire = (handles: BriefHandles): AnswerWire =>
  answerWire('brief_answer', briefAnswerWireShape(handles));

const answerSchema = BRIEF_ANSWER_WIRE_SHAPE.extend({
  summary: z.array(z.string()).min(1).max(MAX_BRIEF_LIST_ITEMS),
  recommendations: z.array(z.string()).min(1).max(MAX_BRIEF_LIST_ITEMS),
});

/**
 * The rules a synthesis's answer can break, each the reason `readBriefAnswer` refuses it:
 *
 * - `document`: not one JSON document;
 * - `shape`: not the answer format's shape, a list's bounds included;
 * - `cards`: not one card per research question, in order, each naming its own;
 * - `not_settled_count`: more Not settled entries than a Brief holds;
 * - `forced_not_settled`: a research question not asked or not answered whose card is not
 *   `not_settled` with no visual and no quotes;
 * - `empty`: a passage that is empty once trimmed;
 * - `gate`: a passage the numeral and quotation gate refuses;
 * - `token`: a token that does not resolve, names a theme behind "+K more", or names a poll or
 *   theme outside its card;
 * - `segments`: a passage of more segments than a stored passage holds;
 * - `quote`: a card's quotes not its own research question's, repeated, or too many;
 * - `visual`: a card's visual not one of its polls, or its themes where it has none;
 * - `not_settled_entry`: an entry naming an unknown research question or one named already, an
 *   unsettled entry that says nothing, or a `not_settled` card with no entry.
 */
export const BRIEF_ANSWER_RULES = [
  'document',
  'shape',
  'cards',
  'not_settled_count',
  'forced_not_settled',
  'empty',
  'gate',
  'token',
  'segments',
  'quote',
  'visual',
  'not_settled_entry',
] as const;

export type BriefAnswerRule = (typeof BRIEF_ANSWER_RULES)[number];

/** What a token may name: every poll and theme, or only one research question's. */
export interface BriefProseScope {
  polls: ReadonlySet<string>;
  themes: ReadonlySet<string>;
}

/**
 * Which themes a passage may name: `shown`, the ones a themes visual shows before its "+K more",
 * for the Brief's own words; `kept`, every theme with a handle, for a follow-up answer.
 */
export interface BriefProseThemes {
  themes: 'shown' | 'kept';
}

const themesIn = (rq: Pick<BriefRqHandle, 'themes'>, { themes }: BriefProseThemes): string[] =>
  themes === 'shown' ? rqThemeHandles(rq) : rqKeptThemeHandles(rq);

/** Every poll, and every theme `which` admits, of the study: the scope of a passage not on a card. */
export const wholeStudyProseScope = (
  handles: BriefHandles,
  which: BriefProseThemes,
): BriefProseScope => ({
  polls: new Set(handles.polls.keys()),
  themes: new Set(handles.rqs.flatMap((rq) => themesIn(rq, which))),
});

/** One research question's polls, and the themes `which` admits: the scope of its card's passages. */
export const rqProseScope = (
  rq: Pick<BriefRqHandle, 'polls' | 'themes'>,
  which: BriefProseThemes,
): BriefProseScope => ({
  polls: new Set(rq.polls),
  themes: new Set(themesIn(rq, which)),
});

/** Whether a token's poll or theme is one `scope` lets a passage name. */
export const briefTokenInScope = (token: BriefToken, scope: BriefProseScope): boolean =>
  'theme' in token
    ? scope.themes.has(`THEME${String(token.theme)}`)
    : scope.polls.has(`POLL${String(token.poll)}`);

/**
 * What a token names, as ids: `null` when its handle is not the study's, names an option its poll
 * does not have, or reaches outside `scope` (`briefTokenInScope`).
 */
export const resolveBriefToken = (
  token: BriefToken,
  handles: BriefHandles,
  scope: BriefProseScope,
): BriefProseSegment | null => {
  if (!briefTokenInScope(token, scope)) {
    return null;
  }
  if ('theme' in token) {
    const theme = handles.themes.get(`THEME${String(token.theme)}`);
    return theme === undefined ? null : { t: 'count', ref: { theme: theme.theme_id } };
  }

  const poll = handles.polls.get(`POLL${String(token.poll)}`);
  if (poll === undefined) {
    return null;
  }
  if (token.kind === 'of') {
    return { t: 'of', poll: poll.dg_question_id };
  }
  const option = poll.options[token.option - 1];
  if (option === undefined) {
    return null;
  }
  return token.kind === 'count'
    ? { t: 'count', ref: { poll: poll.dg_question_id, option: option.option_id } }
    : { t: 'option', poll: poll.dg_question_id, option: option.option_id };
};

/**
 * What a passage's counts, totals and option wordings are filled in from: each poll by its
 * question's id, with its respondents and each option's wording and count, and each theme by its id.
 */
export interface BriefValues {
  polls: ReadonlyMap<
    string,
    {
      respondents: number;
      options: readonly { option_id: string; text: string; count: number }[];
    }
  >;
  themes: ReadonlyMap<string, { participants: number }>;
}

/**
 * A read segment as stored, its value filled in from `values`: the one place a Brief's and a
 * follow-up answer's counts, totals and option wordings are given what they show.
 */
export const valuedBriefSegment = (
  segment: BriefProseSegment,
  values: BriefValues,
): RichTextSegment => {
  switch (segment.t) {
    case 'text':
      return segment;
    case 'of':
      return {
        t: 'of',
        poll: segment.poll,
        value: values.polls.get(segment.poll)?.respondents ?? 0,
      };
    case 'option': {
      const text = values.polls
        .get(segment.poll)
        ?.options.find((each) => each.option_id === segment.option)?.text;
      return { t: 'option', poll: segment.poll, option: segment.option, text: text ?? '' };
    }
    case 'count': {
      const { ref } = segment;
      const value =
        'theme' in ref
          ? values.themes.get(ref.theme)?.participants
          : values.polls.get(ref.poll)?.options.find((each) => each.option_id === ref.option)
              ?.count;
      return { t: 'count', ref, value: value ?? 0 };
    }
  }
};

/** The values a passage's tokens are filled in from, as the handles number them. */
export const briefValuesOf = (handles: BriefHandles): BriefValues => ({
  polls: new Map([...handles.polls.values()].map((poll) => [poll.dg_question_id, poll])),
  themes: new Map([...handles.themes.values()].map((theme) => [theme.theme_id, theme])),
});

type BriefTextSegment = { t: 'text'; text: string };

const isTextSegment = (segment: { t: string }): segment is BriefTextSegment => segment.t === 'text';

/**
 * A passage cut to `max` characters of the synthesis's own words, at the last word boundary that
 * leaves room for an ellipsis, with whatever followed dropped. A passage is the synthesis's
 * explanation, so one that runs long is shortened rather than costing the whole Brief.
 */
export const boundedBriefProse = <Segment extends { t: string }>(
  segments: readonly (Segment | BriefTextSegment)[],
  max: number,
): (Segment | BriefTextSegment)[] => {
  const kept: (Segment | BriefTextSegment)[] = [];
  let used = 0;
  for (const segment of segments) {
    if (!isTextSegment(segment)) {
      kept.push(segment);
      continue;
    }
    if (used + segment.text.length <= max) {
      kept.push(segment);
      used += segment.text.length;
      continue;
    }
    const room = segment.text.slice(0, Math.max(0, max - used - 1));
    const boundary = room.lastIndexOf(' ');
    const cut = (boundary > 0 ? room.slice(0, boundary) : room).trimEnd();
    kept.push({ t: 'text', text: `${cut}…` });
    return kept;
  }
  return kept;
};

/**
 * One passage, read: in its canonical form (`gatedBriefProse`), its tokens resolved inside `scope`,
 * its words through the numeral gate, cut to `max`. Or the rule it breaks: `empty` for a passage
 * that is empty once trimmed; `gate` for a character outside the canonical form's, a bare numeral or
 * a quoted passage; `token` for a token that does not resolve or reaches outside `scope`; `segments`
 * for more segments than a stored passage holds. The words kept are the canonical ones the gate
 * read.
 */
const readProse = (
  text: string,
  handles: BriefHandles,
  scope: BriefProseScope,
  max: number,
): BriefProse | 'empty' | 'gate' | 'token' | 'segments' => {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return 'empty';
  }
  const parts = gatedBriefProse(trimmed);
  if (parts === null) {
    return 'gate';
  }

  const segments: BriefProse = [];
  for (const part of parts) {
    if (part.t === 'text') {
      segments.push({ t: 'text', text: part.text });
      continue;
    }
    const resolved = resolveBriefToken(part.token, handles, scope);
    if (resolved === null) {
      return 'token';
    }
    segments.push(resolved);
  }

  const bounded = boundedBriefProse(segments, max);

  return bounded.length > MAX_RICH_TEXT_SEGMENTS ? 'segments' : bounded;
};

/** An answer refused: the rule it broke and where. */
interface BriefAnswerRefusal {
  ok: false;
  rule: BriefAnswerRule;
  at: string;
}

const refused = (rule: BriefAnswerRule, at: string): BriefAnswerRefusal => ({
  ok: false,
  rule,
  at,
});

const isRefusal = (value: object): value is BriefAnswerRefusal => 'rule' in value;

/**
 * The Brief a synthesis's answer gives, or the rule it breaks (`BRIEF_ANSWER_RULES`) and where.
 *
 * The whole answer must be one JSON document (`readAnswerDocument`), optionally inside one code
 * fence, in exactly the answer format. Beyond its shape:
 *
 * - exactly one card per research question, in order, each naming its own (`RQ1`, `RQ2`, …);
 * - a research question the guide never asked, or that nobody answered, is `not_settled`, with no
 *   visual and no quotes;
 * - every `not_settled` card has a Not settled entry naming it, at most one entry names any research
 *   question, and an entry says what is unsettled unless nobody asked or answered it;
 * - every token resolves, names only a theme its themes visual shows before "+K more", and a
 *   card's tokens name only its own research question's polls and themes; the verdict, summary,
 *   recommendations and Not settled may name any of the study's;
 * - a card's quotes are its own research question's, at most `BRIEF_CARD_QUOTE_LIMIT`, each once;
 * - a card's visual is one of its polls, or its themes where they are available;
 * - every passage passes the numeral gate.
 *
 * Over-long passages are cut, not refused (`boundedBriefProse`); every other bound the stored Brief
 * has is refused here, so every answer this reads composes. Handles become ids here; the synthesis
 * writes none.
 */
export const readBriefAnswer = (
  answer: string,
  handles: BriefHandles,
): AnswerReading<BriefAnswer, BriefAnswerRule> => {
  const document = readAnswerDocument(unfencedAnswer(answer));

  return document === null
    ? { ok: false, rule: 'document' }
    : readBriefAnswerValue(document.value, handles, { themes: 'shown' });
};

/**
 * `readBriefAnswer` past its one-document check: the Brief a value already read from one JSON
 * document gives, for a reader that read the document itself, or the rule it breaks. `which` says
 * which themes its passages may name: `shown` for a synthesis, `kept` for a revision written after
 * a follow-up, whose reader can open "+K more".
 */
export const readBriefAnswerValue = (
  value: unknown,
  handles: BriefHandles,
  which: BriefProseThemes,
): AnswerReading<BriefAnswer, BriefAnswerRule> => {
  const parsed = answerSchema.safeParse(value);
  if (!parsed.success) {
    return refusedShape(parsed.error);
  }
  const { data } = parsed;
  if (data.cards.length !== handles.rqs.length) {
    return refused('cards', 'cards');
  }
  if (data.not_settled.length > MAX_BRIEF_ENTRIES) {
    return refused('not_settled_count', 'not_settled');
  }

  const passage = (
    text: string,
    scope: BriefProseScope,
    max: number,
    at: string,
  ): BriefProse | BriefAnswerRefusal => {
    const prose = readProse(text, handles, scope, max);
    return typeof prose === 'string' ? refused(prose, at) : prose;
  };
  const passages = (
    texts: readonly string[],
    scope: BriefProseScope,
    at: string,
  ): BriefProse[] | BriefAnswerRefusal => {
    const read: BriefProse[] = [];
    for (const [index, text] of texts.entries()) {
      const prose = passage(text, scope, MAX_BRIEF_PASSAGE_CHARS, `${at}.${String(index)}`);
      if (isRefusal(prose)) {
        return prose;
      }
      read.push(prose);
    }
    return read;
  };
  const everything = wholeStudyProseScope(handles, which);

  const headline = passage(
    data.verdict.headline,
    everything,
    MAX_BRIEF_HEADLINE_CHARS,
    'verdict.headline',
  );
  if (isRefusal(headline)) {
    return headline;
  }
  const subVerdict = passage(
    data.verdict.sub_verdict,
    everything,
    MAX_BRIEF_PASSAGE_CHARS,
    'verdict.sub_verdict',
  );
  if (isRefusal(subVerdict)) {
    return subVerdict;
  }
  const summary = passages(data.summary, everything, 'summary');
  if (isRefusal(summary)) {
    return summary;
  }
  const recommendations = passages(data.recommendations, everything, 'recommendations');
  if (isRefusal(recommendations)) {
    return recommendations;
  }

  const cards: BriefAnswer['cards'] = [];
  for (const [index, rq] of handles.rqs.entries()) {
    const card = data.cards[index];
    const at = `cards.${String(index)}`;
    if (card === undefined || card.rq !== rq.handle) {
      return refused('cards', `${at}.rq`);
    }
    if (
      rq.state !== 'answered' &&
      (card.status !== 'not_settled' || card.visual !== null || card.quotes.length > 0)
    ) {
      return refused('forced_not_settled', at);
    }

    const scope = rqProseScope(rq, which);
    const cardHeadline = passage(card.headline, scope, MAX_BRIEF_HEADLINE_CHARS, `${at}.headline`);
    if (isRefusal(cardHeadline)) {
      return cardHeadline;
    }
    const detail = passage(card.detail, scope, MAX_BRIEF_PASSAGE_CHARS, `${at}.detail`);
    if (isRefusal(detail)) {
      return detail;
    }

    if (
      card.quotes.length > BRIEF_CARD_QUOTE_LIMIT ||
      new Set(card.quotes).size !== card.quotes.length ||
      card.quotes.some((quote) => !rq.quotes.includes(quote))
    ) {
      return refused('quote', `${at}.quotes`);
    }

    let visual: BriefAnswerVisual = null;
    if (card.visual === BRIEF_THEMES_VISUAL) {
      if (rqThemeHandles(rq).length === 0) {
        return refused('visual', `${at}.visual`);
      }
      visual = { kind: 'themes' };
    } else if (card.visual !== null) {
      const poll = rq.polls.includes(card.visual) ? handles.polls.get(card.visual) : undefined;
      if (poll === undefined) {
        return refused('visual', `${at}.visual`);
      }
      visual = { kind: 'poll', dg_question_id: poll.dg_question_id };
    }

    cards.push({
      rq_id: rq.rq_id,
      status: card.status,
      headline: cardHeadline,
      detail,
      visual,
      quote_ids: card.quotes,
    });
  }

  const notSettled: BriefAnswer['not_settled'] = [];
  const named = new Set<string>();
  for (const [index, entry] of data.not_settled.entries()) {
    const at = `not_settled.${String(index)}`;
    const rq = entry.rq === null ? null : handles.rqs.find((each) => each.handle === entry.rq);
    if (rq === undefined || (rq !== null && named.has(rq.handle))) {
      return refused('not_settled_entry', `${at}.rq`);
    }
    if (rq !== null) {
      named.add(rq.handle);
    }

    const kind: NotSettledKind = rq === null || rq.state === 'answered' ? 'unsettled' : rq.state;
    const followUp = passage(
      entry.follow_up,
      everything,
      MAX_BRIEF_PASSAGE_CHARS,
      `${at}.follow_up`,
    );
    if (isRefusal(followUp)) {
      return followUp;
    }
    let what: BriefProse | null = null;
    if (kind === 'unsettled') {
      if (entry.what === null) {
        return refused('not_settled_entry', `${at}.what`);
      }
      const read = passage(entry.what, everything, MAX_BRIEF_PASSAGE_CHARS, `${at}.what`);
      if (isRefusal(read)) {
        return read;
      }
      what = read;
    }
    notSettled.push({ rq_id: rq?.rq_id ?? null, kind, what, follow_up: followUp });
  }

  const unnamed = handles.rqs.findIndex(
    (rq, index) => data.cards[index]?.status === 'not_settled' && !named.has(rq.handle),
  );
  if (unnamed !== -1) {
    return refused('not_settled_entry', `cards.${String(unnamed)}`);
  }

  return {
    ok: true,
    value: {
      verdict: { headline, sub_verdict: subVerdict },
      summary,
      recommendations,
      not_settled: notSettled,
      cards,
    },
  };
};

/**
 * The Brief `readBriefAnswer` reads from an answer, or `null` for one it refuses: for a reader that
 * needs the Brief and not why an answer is not one.
 */
export const parseBriefAnswer = (answer: string, handles: BriefHandles): BriefAnswer | null => {
  const reading = readBriefAnswer(answer, handles);

  return reading.ok ? reading.value : null;
};
